import React, { useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import {
  BackgroundJobStatusValue,
  isActiveBackgroundJobStatus,
} from 'src/fetch/backgroundJobs/backgroundJobStatus'
import { HStack, Icon, T, VStack } from 'src/govwell-ui'
import type { IconKey } from 'src/govwell-ui/components/Icon/util'

import {
  backgroundJobBarVariantClassName,
  backgroundJobBarVariantTextClassName,
  getJobBarContent,
  getPillRowDisplayMessage,
} from './backgroundJobMessages'
import { getBackgroundJobPillNavigationDestination } from './backgroundJobNavigation'
import PillChromeIconAction from './PillChromeIconAction'
import type { TrackedBackgroundJob } from './types'

/** Mirror `AIAssistantPill` shell overrides — do not change AIAssistantPill itself. */
export const aiAssistantPillShellClassName =
  '!h-auto !rounded-full !py-1 !shadow-sm !transition-all !duration-200 after:!rounded-full after:!inset-shadow-none'

const terminalPillNavigateButtonClassName =
  'inline-flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 overflow-hidden border-0 bg-transparent p-0 text-left text-inherit focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-outline disabled:cursor-not-allowed disabled:opacity-70'

const pillShellClassName =
  'relative inline-flex max-w-[min(400px,calc(100vw-200px))] min-w-0 shrink-0 items-center gap-1.5 overflow-hidden border'

const pillMessageTextClassName = 'block truncate text-[11px] leading-none font-medium'
const pillDescriptionTextClassName =
  'block truncate text-[10px] leading-none font-medium opacity-80'

const dropdownRowTextIndentClassName = 'pl-5'
const dropdownTerminalRowButtonClassName =
  'w-full cursor-pointer rounded-sm border-0 bg-transparent p-0 text-left hover:bg-neutral-50 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-outline disabled:cursor-not-allowed disabled:opacity-70'

const getJobStatusIconKey = (job: TrackedBackgroundJob): IconKey | undefined => {
  if (isActiveBackgroundJobStatus(job.status)) {
    return 'FaSpinnerThird'
  }
  if (job.status === BackgroundJobStatusValue.Completed) {
    return 'FaCheck'
  }
  if (job.status === BackgroundJobStatusValue.PartiallyCompleted) {
    return 'FaCircleExclamation'
  }
  if (job.status === BackgroundJobStatusValue.Failed) {
    return 'FaCircleXmark'
  }
  if (job.status === BackgroundJobStatusValue.Cancelled) {
    return 'FaBan'
  }

  return undefined
}

const shouldShowTerminalNavigateButton = (job: TrackedBackgroundJob) =>
  job.isInTerminalStatus && job.status !== BackgroundJobStatusValue.Cancelled

type Props = {
  isTerminalNavigateDisabled?: boolean
  job: TrackedBackgroundJob
  layout: 'dropdown' | 'pill'
  onCancel?: (event: React.MouseEvent) => void
  onDismiss?: (event: React.MouseEvent) => void
  /** Dropdown row click for terminal jobs (navigate or dismiss-only for cancelled). */
  onDropdownTerminalRowActivate?: () => void | Promise<void>
  onTerminalNavigate?: () => void | Promise<void>
  shouldIncludeRecordTypeInLocation?: boolean
}

const JobStatusIcon = ({
  iconKey,
  isSpinner,
  className,
}: {
  iconKey: IconKey
  isSpinner: boolean
  className?: string
}) => (
  <Icon
    icon={iconKey}
    className={twMerge('size-3.5 shrink-0', isSpinner && 'animate-spin', className)}
  />
)

const BackgroundJobPillRow = ({
  job,
  layout,
  isTerminalNavigateDisabled = false,
  onCancel,
  onDismiss,
  onDropdownTerminalRowActivate,
  onTerminalNavigate,
  shouldIncludeRecordTypeInLocation = true,
}: Props) => {
  const content = useMemo(() => getJobBarContent(job), [job])
  const displayMessage = useMemo(
    () =>
      getPillRowDisplayMessage({
        message: content.message,
        layout,
        job,
        shouldIncludeRecordTypeInLocation,
      }),
    [content.message, job, layout, shouldIncludeRecordTypeInLocation]
  )
  const isInProgress = isActiveBackgroundJobStatus(job.status)
  const isTerminal = job.isInTerminalStatus
  const showTerminalNavigateButton = shouldShowTerminalNavigateButton(job)
  const statusIconKey = getJobStatusIconKey(job)
  const isStatusSpinner = isInProgress

  const statusContent = useMemo(
    () => (
      <>
        {statusIconKey && <JobStatusIcon iconKey={statusIconKey} isSpinner={isStatusSpinner} />}
        <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <span className={pillMessageTextClassName}>{displayMessage}</span>
          {content.description && (
            <span className={pillDescriptionTextClassName}>{content.description}</span>
          )}
        </span>
      </>
    ),
    [content.description, displayMessage, isStatusSpinner, statusIconKey]
  )

  const terminalNavigateAriaLabel = useMemo(() => {
    const listName = getBackgroundJobPillNavigationDestination(job.type).ariaListName
    return content.description
      ? `${displayMessage}. ${content.description}. Return to ${listName}`
      : `${displayMessage}. Return to ${listName}`
  }, [content.description, displayMessage, job.type])

  const dropdownDismissAriaLabel = useMemo(() => {
    const descriptionWithoutTrailingPeriod = content.description?.replace(/\.$/, '')
    return [displayMessage, descriptionWithoutTrailingPeriod, 'Dismiss background job']
      .filter(Boolean)
      .join('. ')
  }, [content.description, displayMessage])

  if (layout === 'dropdown') {
    const rowTextClassName = backgroundJobBarVariantTextClassName(content.variant)
    const dropdownRowBody = (
      <VStack align="start" className="w-full py-0.5 text-left" gap={1}>
        <HStack className="w-full" gap={2}>
          {statusIconKey && (
            <JobStatusIcon
              iconKey={statusIconKey}
              isSpinner={isStatusSpinner}
              className={rowTextClassName}
            />
          )}
          <T.Body
            className={twMerge(
              rowTextClassName,
              'min-w-0 flex-1 truncate text-[11px] leading-snug font-medium'
            )}
          >
            {displayMessage}
          </T.Body>
          {isInProgress && onCancel && (
            <PillChromeIconAction
              ariaLabel="Cancel background job"
              icon="FaStop"
              onClick={onCancel}
              tooltip="Cancel"
            />
          )}
        </HStack>
        {content.description && (
          <T.Body
            className={twMerge(
              dropdownRowTextIndentClassName,
              'truncate text-[10px] leading-snug font-medium text-neutral-600'
            )}
          >
            {content.description}
          </T.Body>
        )}
      </VStack>
    )

    if (isTerminal) {
      return (
        <button
          type="button"
          aria-busy={isTerminalNavigateDisabled}
          aria-label={
            showTerminalNavigateButton ? terminalNavigateAriaLabel : dropdownDismissAriaLabel
          }
          className={twMerge('w-full', dropdownTerminalRowButtonClassName)}
          disabled={isTerminalNavigateDisabled}
          onClick={() => void onDropdownTerminalRowActivate?.()}
        >
          {dropdownRowBody}
        </button>
      )
    }

    return dropdownRowBody
  }

  const hasTrailingAction = (isInProgress && onCancel) || (isTerminal && onDismiss)

  return (
    <span
      className={twMerge(
        pillShellClassName,
        aiAssistantPillShellClassName,
        hasTrailingAction ? '!pr-1 !pl-2.5' : '!px-3',
        backgroundJobBarVariantClassName(content.variant)
      )}
      {...(showTerminalNavigateButton
        ? {}
        : {
            'aria-label': displayMessage,
            role: 'status' as const,
          })}
    >
      {showTerminalNavigateButton ? (
        <button
          type="button"
          aria-busy={isTerminalNavigateDisabled}
          aria-label={terminalNavigateAriaLabel}
          className={terminalPillNavigateButtonClassName}
          disabled={isTerminalNavigateDisabled}
          onClick={() => void onTerminalNavigate?.()}
        >
          {statusContent}
        </button>
      ) : (
        statusContent
      )}
      {isInProgress && onCancel && (
        <PillChromeIconAction
          ariaLabel="Cancel background job"
          icon="FaStop"
          onClick={onCancel}
          tooltip="Cancel"
        />
      )}
      {isTerminal && onDismiss && (
        <PillChromeIconAction
          ariaLabel="Dismiss background job"
          icon="FaXmark"
          onClick={onDismiss}
          tooltip="Dismiss"
        />
      )}
    </span>
  )
}

export default React.memo(BackgroundJobPillRow)
